import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'styled-components';
import {
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
  BookOpen,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Library,
  Download,
  Loader,
  Menu,
  X
} from 'lucide-react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import audioService from './services/audioService';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  background: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div<{ isOpen: boolean }>`
  width: 300px;
  background: #2c3e50;
  color: white;
  padding: 20px;
  overflow-y: auto;
  box-shadow: 2px 0 10px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    width: 280px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: #3498db;
  color: white;
  border: none;
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const MobileOverlay = styled.div<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 999;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Toolbar = styled.div`
  background: white;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    padding: 10px 15px;
    gap: 10px;
  }
`;

const PDFContainer = styled.div`
  flex: 1;
  overflow: auto;
  background: #f8f9fa;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const PDFWrapper = styled.div<{ scale: number }>`
  transform: scale(${props => props.scale});
  transform-origin: top center;
  transition: transform 0.2s ease;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  max-width: 100%;
`;

const AudioControls = styled.div`
  background: white;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 10px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary'; loading?: boolean }>`
  background: ${props => props.variant === 'primary' ? '#3498db' : '#ecf0f1'};
  color: ${props => props.variant === 'primary' ? 'white' : '#2c3e50'};
  border: none;
  padding: 10px 15px;
  border-radius: 6px;
  cursor: ${props => props.loading ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  opacity: ${props => props.loading ? 0.7 : 1};

  &:hover {
    background: ${props => props.loading ? undefined : (props.variant === 'primary' ? '#2980b9' : '#d5dbdb')};
    transform: ${props => props.loading ? 'none' : 'translateY(-1px)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const PageAudioButton = styled(Button)`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: rgba(52, 152, 219, 0.9);
  color: white;
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(41, 128, 185, 0.9);
  }
`;

const DocumentLibrary = styled.div`
  margin-bottom: 20px;
`;

const DocumentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
`;

const DocumentCard = styled.div<{ selected?: boolean }>`
  background: ${props => props.selected ? '#3498db' : '#34495e'};
  padding: 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.selected ? '#2980b9' : '#4a6741'};
  }

  h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 0 0 8px 0;
    font-size: 12px;
    opacity: 0.8;
    line-height: 1.4;
  }

  .category {
    font-size: 11px;
    background: rgba(255,255,255,0.2);
    padding: 2px 8px;
    border-radius: 12px;
    display: inline-block;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 6px;
  background: #ecf0f1;
  border-radius: 3px;
  overflow: hidden;
  cursor: pointer;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: #3498db;
  width: ${props => props.progress}%;
  transition: width 0.1s ease;
`;

const VolumeSlider = styled.input`
  width: 100px;
`;

const PageInfo = styled.div`
  background: #34495e;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SectionList = styled.div`
  margin-top: 20px;
`;

const SectionItem = styled.div<{ active?: boolean }>`
  padding: 12px;
  background: ${props => props.active ? '#3498db' : '#34495e'};
  margin-bottom: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#2980b9' : '#4a6741'};
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  .spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const StatusMessage = styled.div<{ type: 'info' | 'error' | 'success' }>`
  padding: 10px 15px;
  border-radius: 6px;
  margin: 10px 0;
  background: ${props => {
    switch (props.type) {
      case 'error': return '#e74c3c';
      case 'success': return '#27ae60';
      default: return '#3498db';
    }
  }};
  color: white;
  font-size: 14px;
`;

interface AudioState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  currentAudioUrl?: string;
  isGenerating: boolean;
}

interface PDFSection {
  id: string;
  title: string;
  page: number;
  startY: number;
  endY: number;
}

interface TripitakaDocument {
  id: string;
  title: string;
  description: string;
  filename: string;
  category: string;
  subcategory?: string;
  volume?: string;
  pages: number;
  sections: PDFSection[];
  language: string;
  year?: string;
}

interface DocumentCategory {
  id: string;
  title: string;
  description: string;
  subcategories: DocumentSubcategory[];
}

interface DocumentSubcategory {
  id: string;
  title: string;
  description: string;
  documents: TripitakaDocument[];
}

const App: React.FC = () => {
  const [selectedDocument, setSelectedDocument] = useState<TripitakaDocument | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(window.innerWidth <= 768 ? 0.7 : 1.0);
  const [rotation, setRotation] = useState<number>(0);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [audioState, setAudioState] = useState<AudioState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 0.7,
    isMuted: false,
    isGenerating: false
  });
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'info' | 'error' | 'success' } | null>(null);
  const [backendHealthy, setBackendHealthy] = useState<boolean>(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  const audioRef = useRef<HTMLAudioElement>(null);

  // Check backend health on startup
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const healthy = await audioService.healthCheck();
        setBackendHealthy(healthy);
        if (healthy) {
          setStatusMessage({ text: 'S3 audio access ready', type: 'success' });
        }
      } catch (error) {
        setBackendHealthy(true); // For S3-only setup, always consider healthy
        setStatusMessage({ text: 'S3 audio access ready', type: 'success' });
      }
    };
    
    checkHealth();
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hierarchical Tripitaka document library
  const documentCategories: DocumentCategory[] = [
    {
      id: 'vinaya',
      title: 'Vinaya Pitaka',
      description: 'The Basket of Discipline - Rules and regulations for monastic community',
      subcategories: [
        {
          id: 'suttavibhanga',
          title: 'Suttavibhanga',
          description: 'Analysis of the rules',
          documents: []
        }
      ]
    },
    {
      id: 'sutta',
      title: 'Sutta Pitaka',
      description: 'The Basket of Discourses - Buddha\'s teachings and discourses',
      subcategories: [
        {
          id: 'digha-nikaya',
          title: 'Digha Nikaya',
          description: 'Long Discourses',
          documents: []
        }
      ]
    },
    {
      id: 'abhidhamma',
      title: 'Abhidhamma Pitaka',
      description: 'The Basket of Higher Doctrine - Philosophical and psychological analysis',
      subcategories: [
        {
          id: 'vibhanga-pakarana',
          title: 'Vibhanga Pakarana',
          description: 'Book of Analysis - Detailed analysis of mental and physical phenomena',
          documents: [
            {
              id: 'vibhanga-vol1',
              title: 'Vibhanga Pakarana - Volume 1',
              description: 'First volume of the Book of Analysis containing detailed exposition of mental and physical phenomena',
              filename: '/documents/abhidhamma-pitaka/vibhanga-pakarana/volume-1.pdf',
              category: 'Abhidhamma Pitaka',
              subcategory: 'Vibhanga Pakarana',
              volume: 'Volume 1',
              pages: 1000,
              language: 'Sinhala',
              year: '2009',
              sections: [
                { id: '1', title: 'Khandha Vibhanga', page: 1, startY: 0, endY: 200 },
                { id: '2', title: 'Ayatana Vibhanga', page: 50, startY: 0, endY: 300 },
                { id: '3', title: 'Dhatu Vibhanga', page: 100, startY: 0, endY: 250 },
                { id: '4', title: 'Sacca Vibhanga', page: 150, startY: 0, endY: 400 },
                { id: '5', title: 'Indriya Vibhanga', page: 200, startY: 0, endY: 350 },
                { id: '6', title: 'Paticcasamuppada Vibhanga', page: 250, startY: 0, endY: 300 },
                { id: '7', title: 'Satipatthana Vibhanga', page: 300, startY: 0, endY: 450 },
                { id: '8', title: 'Sammappadhana Vibhanga', page: 350, startY: 0, endY: 200 },
                { id: '9', title: 'Iddhipada Vibhanga', page: 400, startY: 0, endY: 300 },
                { id: '10', title: 'Bojjhanga Vibhanga', page: 450, startY: 0, endY: 350 },
              ]
            }
          ]
        },
        {
          id: 'dhammasangani',
          title: 'Dhammasangani',
          description: 'Enumeration of Phenomena',
          documents: []
        },
        {
          id: 'dhatukatha',
          title: 'Dhatukatha',
          description: 'Discussion of Elements',
          documents: []
        }
      ]
    }
  ];

  // Flatten documents for easy access
  const allDocuments = documentCategories.flatMap(category =>
    category.subcategories.flatMap(subcategory => subcategory.documents)
  );

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  }, []);

  const onDocumentSelect = useCallback((document: TripitakaDocument) => {
    setSelectedDocument(document);
    setCurrentPage(1);
    setSelectedSection(null);
    setAudioState(prev => ({
      ...prev,
      duration: 0,
      currentTime: 0,
      isPlaying: false,
      currentAudioUrl: undefined
    }));
    setStatusMessage(null);
  }, []);

  // Generate audio for current page
  const handleGeneratePageAudio = useCallback(async () => {
    if (!selectedDocument) {
      setStatusMessage({ text: 'No document selected', type: 'error' });
      return;
    }

    setAudioState(prev => ({ ...prev, isGenerating: true }));
    setStatusMessage({ text: `Generating audio for page ${currentPage}...`, type: 'info' });

    try {
      const audioMetadata = await audioService.generatePageAudio(selectedDocument.id, currentPage);
      
      setAudioState(prev => ({
        ...prev,
        currentAudioUrl: audioMetadata.audioUrl,
        duration: audioMetadata.duration || 0,
        currentTime: 0,
        isPlaying: false,
        isGenerating: false
      }));

      setStatusMessage({
        text: `Audio generated for page ${currentPage}${audioMetadata.s3Key ? ' and uploaded to S3' : ''}`,
        type: 'success'
      });

      // Clear success message after 3 seconds
      setTimeout(() => setStatusMessage(null), 3000);

    } catch (error) {
      console.error('Failed to generate audio:', error);
      setAudioState(prev => ({ ...prev, isGenerating: false }));
      setStatusMessage({ text: `Failed to generate audio: ${error}`, type: 'error' });
    }
  }, [selectedDocument, currentPage]);

  // Load existing audio for current page
  const handleLoadPageAudio = useCallback(async () => {
    if (!selectedDocument) return;

    try {
      const audioUrl = await audioService.getPageAudio(selectedDocument.id, currentPage);
      
      setAudioState(prev => ({
        ...prev,
        currentAudioUrl: audioUrl,
        duration: 0, // Will be set when audio loads
        currentTime: 0,
        isPlaying: false
      }));

    } catch (error) {
      console.error('Failed to load audio:', error);
      // Don't show error message, just silently fail for missing audio
      setAudioState(prev => ({
        ...prev,
        currentAudioUrl: undefined
      }));
    }
  }, [selectedDocument, currentPage]);

  // Audio playback controls
  const handlePlayPause = useCallback(() => {
    if (!audioRef.current || !audioState.currentAudioUrl) return;

    if (audioState.isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  }, [audioState.isPlaying, audioState.currentAudioUrl]);

  const handleStop = useCallback(() => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setAudioState(prev => ({ ...prev, isPlaying: false, currentTime: 0 }));
  }, []);

  const handleVolumeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(event.target.value);
    setAudioState(prev => ({ ...prev, volume, isMuted: volume === 0 }));
    
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, []);

  const handleMute = useCallback(() => {
    const newMuted = !audioState.isMuted;
    setAudioState(prev => ({ ...prev, isMuted: newMuted }));
    
    if (audioRef.current) {
      audioRef.current.muted = newMuted;
    }
  }, [audioState.isMuted]);

  const handleSectionSelect = useCallback((section: PDFSection) => {
    setSelectedSection(section.id);
    setCurrentPage(section.page);
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  }, []);

  const handleRotate = useCallback(() => {
    setRotation(prev => (prev + 90) % 360);
  }, []);

  // Audio event handlers
  const onAudioPlay = useCallback(() => {
    setAudioState(prev => ({ ...prev, isPlaying: true }));
  }, []);

  const onAudioPause = useCallback(() => {
    setAudioState(prev => ({ ...prev, isPlaying: false }));
  }, []);

  const onAudioTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    setAudioState(prev => ({ 
      ...prev, 
      currentTime: audioRef.current?.currentTime || 0,
      duration: audioRef.current?.duration || 0
    }));
  }, []);

  const onAudioLoadedMetadata = useCallback(() => {
    if (!audioRef.current) return;
    setAudioState(prev => ({ ...prev, duration: audioRef.current?.duration || 0 }));
  }, []);

  // Load audio when page changes
  useEffect(() => {
    if (selectedDocument) {
      handleLoadPageAudio();
    }
  }, [selectedDocument, currentPage, handleLoadPageAudio]);

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AppContainer>
      <MobileOverlay isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <Sidebar isOpen={sidebarOpen}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Tripitaka Reader</h2>
          {isMobile && (
            <Button 
              style={{ 
                background: 'transparent',
                color: 'white',
                padding: '8px'
              }} 
              onClick={() => setSidebarOpen(false)}
            >
              <X size={20} />
            </Button>
          )}
        </div>
        
        {backendHealthy && (
          <StatusMessage type="success">
            Audio playback ready - S3 files available
          </StatusMessage>
        )}
        
        {statusMessage && (
          <StatusMessage type={statusMessage.type}>
            {statusMessage.text}
          </StatusMessage>
        )}
        
        <DocumentLibrary>
          <h3><Library size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Document Library</h3>
          <DocumentGrid>
            {allDocuments.map(document => (
              <DocumentCard
                key={document.id}
                selected={selectedDocument?.id === document.id}
                onClick={() => onDocumentSelect(document)}
              >
                <h4>{document.title}</h4>
                <p>{document.description}</p>
                <div className="category">{document.category}</div>
              </DocumentCard>
            ))}
          </DocumentGrid>
        </DocumentLibrary>

        <PageInfo>
          <h3>Document Info</h3>
          {selectedDocument ? (
            <>
              <p><strong>Document:</strong> {selectedDocument.title}</p>
              <p><strong>Pages:</strong> {numPages}</p>
              <p><strong>Current:</strong> {currentPage}</p>
              <p><strong>Audio Mode:</strong> <span style={{ color: '#3498db' }}>
                S3 Direct Access
              </span></p>
            </>
          ) : (
            <p>No document selected</p>
          )}
        </PageInfo>

        <SectionList>
          <h3>Audio Sections</h3>
          {selectedDocument?.sections.map(section => (
            <SectionItem
              key={section.id}
              active={selectedSection === section.id}
              onClick={() => handleSectionSelect(section)}
            >
              <div><strong>{section.title}</strong></div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                Page {section.page}
              </div>
            </SectionItem>
          )) || <p style={{ opacity: 0.7, fontSize: '14px' }}>Select a document to view sections</p>}
        </SectionList>
      </Sidebar>

      <MainContent>
        <Toolbar>
          <MobileMenuButton onClick={() => setSidebarOpen(true)}>
            <Menu size={20} />
            Library
          </MobileMenuButton>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BookOpen size={20} />
            <span style={{ fontWeight: '600' }}>
              {selectedDocument ? selectedDocument.title : 'Tripitaka PDF Reader'}
            </span>
          </div>

          <Button onClick={handleZoomOut} disabled={scale <= 0.5}>
            <ZoomOut size={16} />
          </Button>
          <span>{Math.round(scale * 100)}%</span>
          <Button onClick={handleZoomIn} disabled={scale >= 3.0}>
            <ZoomIn size={16} />
          </Button>

          <Button onClick={handleRotate}>
            <RotateCw size={16} />
            Rotate
          </Button>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
            <Button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage <= 1}
            >
              <SkipBack size={16} />
              Previous
            </Button>
            <Button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, numPages))}
              disabled={currentPage >= numPages}
            >
              Next
              <SkipForward size={16} />
            </Button>
          </div>
        </Toolbar>

        <PDFContainer>
          {selectedDocument ? (
            <Document
              file={selectedDocument.filename}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <LoadingSpinner>
                  <div className="spinner" />
                </LoadingSpinner>
              }
            >
              <PDFWrapper scale={scale}>
                <Page
                  pageNumber={currentPage}
                  rotate={rotation}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
                
                {/* Audio generation button overlay - only show if no audio exists */}
                {!audioState.currentAudioUrl && (
                  <PageAudioButton
                    onClick={handleGeneratePageAudio}
                    disabled={audioState.isGenerating}
                    loading={audioState.isGenerating}
                    title={`Generate audio for page ${currentPage}`}
                  >
                    {audioState.isGenerating ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <Download size={16} />
                    )}
                    {audioState.isGenerating ? 'Generating...' : 'Generate Audio'}
                  </PageAudioButton>
                )}
              </PDFWrapper>
            </Document>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#7f8c8d'
            }}>
              <Library size={64} style={{ marginBottom: '20px' }} />
              <h2>Select a document to get started</h2>
              <p>Choose from the Tripitaka collection in the sidebar</p>
            </div>
          )}
        </PDFContainer>

        <AudioControls>
          <Button 
            variant="primary" 
            onClick={handlePlayPause}
            disabled={!audioState.currentAudioUrl}
          >
            {audioState.isPlaying ? <Pause size={16} /> : <Play size={16} />}
            {audioState.isPlaying ? 'Pause' : 'Play'}
          </Button>

          <Button onClick={handleStop} disabled={!audioState.currentAudioUrl}>
            <Square size={16} />
            Stop
          </Button>

          <ProgressBar>
            <ProgressFill 
              progress={audioState.duration > 0 ? (audioState.currentTime / audioState.duration) * 100 : 0} 
            />
          </ProgressBar>

          <span style={{ minWidth: '80px', fontSize: '14px' }}>
            {formatTime(audioState.currentTime)} / {formatTime(audioState.duration)}
          </span>

          <Button onClick={handleMute}>
            {audioState.isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </Button>

          <VolumeSlider
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={audioState.isMuted ? 0 : audioState.volume}
            onChange={handleVolumeChange}
          />
        </AudioControls>
      </MainContent>

      {/* Hidden audio element for playback */}
      {audioState.currentAudioUrl && (
        <audio
          ref={audioRef}
          src={audioState.currentAudioUrl}
          onPlay={onAudioPlay}
          onPause={onAudioPause}
          onTimeUpdate={onAudioTimeUpdate}
          onLoadedMetadata={onAudioLoadedMetadata}
          onEnded={() => setAudioState(prev => ({ ...prev, isPlaying: false, currentTime: 0 }))}
          muted={audioState.isMuted}
        />
      )}
    </AppContainer>
  );
};

export default App;